import {
    OperationGroup,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {
    applyShakerDoorLineTransparency,
    applyShakerDoorMaterialsDefault,
    calculateDoorWidthDefault,
    createShakerDoor,
} from 'components/customer/Preview3DCommon/3d-models/shaker';
import {
    generateDoorDrawer,
    generateDoorPosition,
    getDoorFormValues,
} from 'components/customer/Preview3DCommon/values/door';
import {applyImagePath} from 'components/customer/RoomPlanner/helpers/scene';
import {isCorFoldKitDoor} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';
import {Group, Mesh, LineSegments} from 'three';

const getPerimeterSpan = (
    operationGroup: OperationGroup,
    readAxis: (coordinate: {x?: number; y?: number}) => number | undefined
): number | undefined => {
    let min = Infinity;
    let max = -Infinity;
    for (const op of operationGroup.Operations ?? []) {
        if (op.Type !== 'perimeter_shape' || !op.Segments) continue;
        for (const seg of op.Segments) {
            for (const coor of [seg.StartCoor, seg.EndCoor]) {
                if (!coor) continue;
                const value = readAxis(coor);
                if (value === undefined) continue;
                if (value < min) min = value;
                if (value > max) max = value;
            }
        }
    }
    const span = max - min;
    return Number.isFinite(span) && span > 0 ? span : undefined;
};

const getPerimeterWidth = (operationGroup: OperationGroup) =>
    getPerimeterSpan(operationGroup, (coordinate) => coordinate.y);

const getPerimeterHeight = (operationGroup: OperationGroup) =>
    getPerimeterSpan(operationGroup, (coordinate) => coordinate.x);

interface ShakerDoorProps {
    operationGroup: OperationGroup;
    variables: TemplateVariable;
    values: PreviewFormValues;
    exteriorThickness: number;
    carcaseThickness: number;
    applyShakerDoorMaterials?: (model: Group, showTexture: boolean) => void;
    showTexture: boolean;
    isPairDoor: boolean;
    isRightDoor: boolean;
    isBiFoldDoor: boolean;
    isRightLShape: boolean;
    isSplitDoor?: boolean;
    isDoorStylePanel?: boolean;
}

const addShakerDoorWithAnimation = ({
    operationGroup,
    variables,
    values,
    exteriorThickness,
    carcaseThickness,
    applyShakerDoorMaterials,
    showTexture,
    isPairDoor,
    isRightDoor,
    isBiFoldDoor,
    isRightLShape,
    isSplitDoor = false,
    isDoorStylePanel = false,
}: ShakerDoorProps) => {
    const DOOR_PANEL_GAP = 50;
    const DOOR_BEVEL_SIZE = 12;
    const DEFAULT_CABINET_WIDTH = 600;

    const formValues = getDoorFormValues(values);
    const cabinetWidth = values.cabinet_width || DEFAULT_CABINET_WIDTH;

    const pantryDoorWidth =
        variables?.isLShapedPantry && isBiFoldDoor
            ? getPerimeterWidth(operationGroup)
            : undefined;

    const isPerimeterSizedDoor =
        isSplitDoor || isDoorStylePanel || isCorFoldKitDoor(operationGroup);

    const isMirroredPanel =
        isDoorStylePanel && operationGroup.MfgOrientationID === undefined;

    const perimeterDoorWidth = isPerimeterSizedDoor
        ? getPerimeterWidth(operationGroup)
        : undefined;

    const actualDoorWidth =
        perimeterDoorWidth ??
        pantryDoorWidth ??
        calculateDoorWidthDefault(
            values,
            cabinetWidth,
            formValues,
            isRightDoor,
            isPairDoor,
            carcaseThickness,
            operationGroup,
            isRightLShape,
            isBiFoldDoor,
            exteriorThickness,
            Boolean(variables?.is3DoorProduct)
        );

    const valuesWithThickness = {
        ...values,
        cabinet_carc_thickness: carcaseThickness,
        cabinet_ext_thickness: exteriorThickness,
    };

    const doorDrawer = generateDoorDrawer(formValues);
    const doorPosition = generateDoorPosition(
        variables,
        valuesWithThickness,
        formValues
    );
    const perimeterDoorHeight = isPerimeterSizedDoor
        ? getPerimeterHeight(operationGroup)
        : undefined;

    const cabinetHeight =
        perimeterDoorHeight ??
        (doorDrawer?.doorHeight && !variables?.isWallOvenProduct
            ? doorDrawer?.doorHeight
            : doorPosition?.doorHeight);

    const vertRails = values.vert_mid_rail_positions;
    const horiRails = values.hori_mid_rail_positions;

    const adjustedVertRails =
        operationGroup.IsPairDoor && vertRails
            ? vertRails.map((p) => p / 2)
            : vertRails;
    const isTopHangDoor = operationGroup.IsTopHangDoor;

    const excludesCarcaseThickness =
        isPerimeterSizedDoor ||
        operationGroup.IsPairDoor ||
        operationGroup.IsLeftApplianceMainDoor ||
        operationGroup.IsLeftApplianceExtendedDoor ||
        operationGroup.IsBifoldLeftDoor ||
        operationGroup.IsBifoldRightDoor;

    const doorWidth =
        actualDoorWidth + (excludesCarcaseThickness ? 0 : carcaseThickness);

    const isLeftLShapeBifoldPairDoor =
        isBiFoldDoor &&
        !isRightLShape &&
        isPairDoor &&
        Boolean(
            operationGroup.IsBifoldLeftDoor || operationGroup.IsBifoldRightDoor
        );

    // These doors are mirrored on X later (bifold right via scale.x, bifold
    // left via its flipped depth and -90 degree turn), so their side borders
    // are swapped up front to land on the correct sides.
    const isMirroredRightLShapeBifoldDoor =
        isBiFoldDoor &&
        isRightLShape &&
        Boolean(
            operationGroup.IsBifoldRightDoor ||
            (operationGroup.IsBifoldLeftDoor &&
                !operationGroup.IsLeftBifoldExtension)
        );

    const borderWidthLeft = Number(values.border_width_left || 60);
    const borderWidthRight = Number(values.border_width_right || 60);

    const mesh = createShakerDoor({
        width: doorWidth,
        height: cabinetHeight,
        // Only set rows/cols when arrays are provided; when null/undefined use auto defaults
        ...(horiRails != null ? {rows: (horiRails?.length || 0) + 1} : {}),
        ...(adjustedVertRails != null
            ? {
                  cols: (adjustedVertRails?.length || 0) + 1,
              }
            : {}),
        gap: DOOR_PANEL_GAP,
        insetTop: Number(values.border_width_top || 60),
        insetLeft: isMirroredRightLShapeBifoldDoor
            ? borderWidthRight
            : borderWidthLeft,
        insetBottom: Number(values.border_width_bottom || 60),
        insetRight: isMirroredRightLShapeBifoldDoor
            ? borderWidthLeft
            : borderWidthRight,
        depth: exteriorThickness,
        bevelSize: DOOR_BEVEL_SIZE,
        isRightDoor:
            (isPerimeterSizedDoor && !isMirroredPanel) ||
            (isRightDoor && !isLeftLShapeBifoldPairDoor),
        gapHorizontal: Number(values.hori_height || 60),
        gapVertical: Number(values.vert_width || 60),
        vertMidRailPositions: adjustedVertRails ?? null,
        horiMidRailPositions: horiRails ?? null,
        glassSubPanels: (values as Record<string, unknown>).glassSubPanels as
            boolean[][] | null | undefined,
        isTopHangDoor,
    });

    applyShakerDoorMaterials
        ? applyShakerDoorMaterials(mesh, showTexture)
        : applyShakerDoorMaterialsDefault(
              mesh,
              showTexture,
              values.cabinet_ext_image
                  ? values.cabinet_ext_image
                  : applyImagePath(values.ext_material?.image)
          );
    applyShakerDoorLineTransparency(mesh, showTexture);
    mesh.name = 'ShakerDoors';

    if (isDoorStylePanel) {
        mesh.children[0].position.y += cabinetHeight / 2;
    } else if (isPerimeterSizedDoor || isBiFoldDoor) {
        mesh.position.y += cabinetHeight / 2;
    }

    if (isBiFoldDoor) {
        if (isRightLShape) {
            if (operationGroup.IsBifoldLeftDoor) {
                mesh.traverse((child) => {
                    if (
                        (child instanceof Mesh ||
                            child instanceof LineSegments) &&
                        !(child.parent instanceof Mesh)
                    ) {
                        child.scale.z = -Math.abs(child.scale.z);
                    }
                });
            }

            if (isPairDoor) {
                if (operationGroup.IsLeftBifoldExtension) {
                    mesh.traverse((child) => {
                        if (
                            (child instanceof Mesh ||
                                child instanceof LineSegments) &&
                            !(child.parent instanceof Mesh)
                        ) {
                            child.scale.z = Math.abs(child.scale.z);
                        }
                    });
                }
            }
        } else if (
            isPairDoor &&
            operationGroup.IsPairDoor &&
            operationGroup.IsRightDoor
        ) {
            mesh.position.x += doorWidth;
        }
    }

    return mesh;
};

export default addShakerDoorWithAnimation;
